import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils_pharma";
import { Cross } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@pharmacy.in");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="login-page">
      {/* Left visual */}
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1478744919174-118dbd24973e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Apothecary"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(17,77,67,0.85), rgba(11,28,25,0.75))" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white/10 backdrop-blur border border-white/20 grid place-items-center">
              <Cross size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-semibold text-lg" style={{ fontFamily: "Chivo" }}>MediLedger</div>
              <div className="text-xs opacity-80 tracking-widest uppercase">Pharmacy Management</div>
            </div>
          </div>
          <div>
            <h2 className="text-3xl lg:text-4xl font-semibold leading-tight" style={{ fontFamily: "Chivo" }}>
              Every strip counted.
              <br /> Every bill printed. <br />Every rupee tracked.
            </h2>
            <p className="mt-4 opacity-80 max-w-md">
              A no-nonsense pharmacy suite built for Indian counters — GST-ready, thermal-print ready, blazingly fast search.
            </p>
          </div>
          <div className="text-xs opacity-70">© {new Date().getFullYear()} MediLedger</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sign in</div>
            <h1 className="text-3xl mt-1 font-semibold" style={{ fontFamily: "Chivo" }}>
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Log in with the admin credentials to manage your pharmacy.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-destructive" data-testid="login-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full">
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-xs text-muted-foreground border-t border-border pt-4">
            Default admin: <span className="font-mono">admin@pharmacy.in / admin123</span>
          </div>
        </form>
      </div>
    </div>
  );
}
